import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as sched from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { FuzzyGanttChart, FuzzyScheduleResult } from '../charts/FuzzyGanttChart';

interface TeamForecastProps {
  teamId: string;
}

/**
 * Team-scoped forecast view using the FuzzyGanttChart.
 * Uses the shared fuzzy schedule from useSchedule() (same source as deliveries)
 * and filters to nodes where at least one contributing resource maps to a team member.
 */
export const TeamForecast: React.FC<TeamForecastProps> = ({ teamId }) => {
  const { database } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { schedule: fuzzySchedule, loading: scheduleLoading } = useSchedule();
  const [teamMemberIds, setTeamMemberIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!database || !safeData) return;
    setTeamMemberIds(null);
    const team = database.teams()[teamId];
    if (!team) {
      setTeamMemberIds(new Set());
      return;
    }
    team.member_person_ids(safeData, true).then((ids: string[]) => {
      setTeamMemberIds(new Set(ids));
    });
  }, [database, safeData, teamId]);

  const { scheduleResult, nodeToEntity } = useMemo(() => {
    if (!fuzzySchedule || !teamMemberIds || teamMemberIds.size === 0 || !database) {
      return { scheduleResult: null, nodeToEntity: {} };
    }
    const adapted = adaptForTeam(fuzzySchedule as any, teamMemberIds, database);
    const n2e = (fuzzySchedule as any)?.meta?.node_to_entity || {};
    return { scheduleResult: adapted, nodeToEntity: n2e };
  }, [fuzzySchedule, teamMemberIds, database]);

  if (scheduleLoading || teamMemberIds === null) {
    return (
      <div className="flex items-center gap-3 justify-center p-8 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
        Building forecast...
      </div>
    );
  }

  if (!scheduleResult) {
    return (
      <div className="p-6 text-center text-cm-text-muted text-sm">
        No forecast data available for this team.
      </div>
    );
  }

  const unschedulable = scheduleResult.schedule?.unschedulable ?? {};
  const hasItems = Object.keys(scheduleResult.schedule?.items ?? {}).length > 0;
  const hasUnschedulable = Object.keys(unschedulable).length > 0;

  return (
    <div className="space-y-4">
      {hasItems && (
        <FuzzyGanttChart
          result={scheduleResult}
          startDate={new Date()}
          nodeToEntity={nodeToEntity}
          linkBase={workspaceId ? `/workspaces/${workspaceId}` : undefined}
        />
      )}
      {hasUnschedulable && (
        <div className="rounded-xl border border-gray-800 bg-gray-950 p-5">
          <h4 className="text-sm font-semibold text-amber-400/80 mb-3">
            Blocked Items
          </h4>
          <ul className="space-y-2 text-sm">
            {Object.entries(unschedulable).map(([nodeId, item]: [string, any]) => (
              <li key={nodeId} className="flex items-start gap-2 text-gray-400">
                <span className="text-amber-500/60 mt-0.5">&#x26a0;</span>
                <div>
                  <span className="text-gray-200">{item.label}</span>
                  <span className="text-gray-500 ml-2">— {item.reason}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!hasItems && !hasUnschedulable && (
        <div className="p-6 text-center text-cm-text-muted text-sm">
          No forecast data available for this team.
        </div>
      )}
    </div>
  );
};

/**
 * Adapt a DatabaseFuzzyScheduleResult for a team view.
 */
function adaptForTeam(
  dbResult: any,
  teamMemberIds: Set<string>,
  database: sched.WrappedDatabase | null,
): FuzzyScheduleResult {
  const meta = dbResult.meta;
  const scheduleData = dbResult.schedule;
  const stats = dbResult.stats || {};
  const items = scheduleData.items as Record<string, any>;

  const resourceToPerson: Record<string, string> = {};
  const teamResourceIds = new Set<string>();
  for (const [resourceId, personId] of Object.entries(meta.resource_to_person as Record<string, string>)) {
    resourceToPerson[resourceId] = (meta.person_labels as Record<string, string>)[personId] || personId;
    if (teamMemberIds.has(personId)) {
      teamResourceIds.add(resourceId);
    }
  }

  const nodeToEntity = meta?.node_to_entity ?? {};

  // Build a set of entity IDs that team members have manual allocations for,
  // mapped to the person who allocated. This lets us place fully-allocated tasks
  // (milestones with no resource_contributions) into the correct person's pool.
  const entityToAllocatedPerson = new Map<string, string>();
  if (database) {
    for (const personId of teamMemberIds) {
      const allocs: any[] = database.manual_allocations_for_person?.(personId) ?? [];
      for (const pa of allocs) {
        entityToAllocatedPerson.set(pa.entity, personId);
      }
    }
  }

  const filteredItems: Record<string, any> = {};
  // Track which milestone tasks need a synthetic resource for pool assignment
  const milestoneSynthetics: { nodeId: string; personId: string }[] = [];

  for (const [nodeId, item] of Object.entries(items)) {
    const contributions = (item as any).resource_contributions || {};
    const resourceIds = Object.keys(contributions);

    if (resourceIds.some((rid) => teamResourceIds.has(rid))) {
      // Normal case: team member contributes to this item
      filteredItems[nodeId] = item;
    } else if (resourceIds.length === 0) {
      const entity = nodeToEntity[nodeId];
      if (entity?.type === 'Outcome') {
        // Outcomes stay ungrouped
        filteredItems[nodeId] = item;
      } else if (entity?.id && entityToAllocatedPerson.has(entity.id)) {
        // Fully-allocated task — inject synthetic contribution for the allocated person
        const allocPerson = entityToAllocatedPerson.get(entity.id)!;
        milestoneSynthetics.push({ nodeId, personId: allocPerson });
        filteredItems[nodeId] = item;
      }
    }
  }

  const resourceSetByNode: Record<string, string[]> = {};
  for (const [nodeId, item] of Object.entries(filteredItems)) {
    const rids = Object.keys((item as any).resource_contributions).filter(rid => teamResourceIds.has(rid));
    if (rids.length > 0) {
      resourceSetByNode[nodeId] = rids.sort();
    }
  }

  const signatureToResources: Record<string, Set<string>> = {};
  for (const rids of Object.values(resourceSetByNode)) {
    const sig = rids.join(',');
    if (!signatureToResources[sig]) signatureToResources[sig] = new Set();
    for (const rid of rids) signatureToResources[sig].add(rid);
  }

  const poolResources: Record<string, string[]> = {};
  const resourceToPool: Record<string, string> = {};
  const assignedResources = new Set<string>();

  for (const [, members] of Object.entries(signatureToResources)) {
    if (members.size > 1) {
      const poolLabel = [...members]
        .map(rid => resourceToPerson[rid] || rid)
        .sort()
        .join(', ');

      if (!poolResources[poolLabel]) poolResources[poolLabel] = [];
      for (const rid of members) {
        if (!assignedResources.has(rid)) {
          poolResources[poolLabel].push(rid);
          resourceToPool[rid] = poolLabel;
          assignedResources.add(rid);
        }
      }
    }
  }

  for (const resourceId of teamResourceIds) {
    if (!assignedResources.has(resourceId)) {
      const name = resourceToPerson[resourceId] || resourceId;
      if (!poolResources[name]) poolResources[name] = [];
      poolResources[name].push(resourceId);
      resourceToPool[resourceId] = name;
    }
  }

  // Patch milestone tasks: inject a synthetic resource so they land in the
  // correct person's pool instead of falling into ungrouped "Outcomes".
  const patchedItems = { ...filteredItems };
  for (const { nodeId, personId } of milestoneSynthetics) {
    // Find a resource ID that maps to this person
    let targetPool: string | undefined;
    for (const [rid, pid] of Object.entries(meta.resource_to_person as Record<string, string>)) {
      if (pid === personId && resourceToPool[rid]) {
        targetPool = resourceToPool[rid];
        break;
      }
    }
    if (targetPool) {
      const syntheticRid = `__milestone_${nodeId}`;
      poolResources[targetPool] = poolResources[targetPool] ?? [];
      poolResources[targetPool].push(syntheticRid);
      resourceToPool[syntheticRid] = targetPool;
      patchedItems[nodeId] = {
        ...patchedItems[nodeId],
        resource_contributions: { [syntheticRid]: 0 },
      };
    }
  }

  return {
    schedule: {
      items: patchedItems,
      unschedulable: scheduleData.unschedulable || {},
      resolution: scheduleData.resolution,
    },
    meta: {
      node_to_step: {},
      node_labels: meta.node_labels || {},
      resource_to_pool: resourceToPool,
      pool_resources: poolResources,
      conditional_nodes: [],
      warnings: meta.warnings || [],
    },
    stats,
  } as unknown as FuzzyScheduleResult;
}
